import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { delay, Observable, of, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { APIResponse } from 'src/app/core/models/api-response';
import { toHttpParams } from '../utils/http-params.util';
import { IMention } from '../models/mention.models';
import { ConversationClassification, ConversationStatus } from '../models/conversation.models';

/**
 * Handles comment and mention operations (list mentions, create/update/delete comment).
 */
@Injectable({ providedIn: 'root' })
export class CommentService {

  private baseUrl = environment.BASE_URL_API + 'inbox';

  /** Toggle to use NgRx store as mock backend. */
  private useMockData = environment.useMockData ?? false;

  constructor(
    private http: HttpClient,
  ) {}

  listMentions(params: {page: number, limit: number}): Observable<APIResponse<IMention>> {
    const page = params.page ?? 1;
    const limit = params.limit ?? 50;
    const httpParams = toHttpParams({
      page, limit,
    });

    if (this.useMockData) {
      return of(null).pipe(delay(1000), switchMap(() => of<APIResponse<IMention>>({ data: [
        {
          id: 1,
          classification: ConversationClassification.ORDER,
          status: ConversationStatus.OPEN,
          assignee: null,
          thread_id: 1,
          author: { id: 1, name: 'John Doe', email: 'john.doe@example.com' },
          subject: 'Hello, world!',
          comment: '@John Doe: Hello, world!',
          created_at: '2021-01-01T00:00:00Z',
          is_read: false,
        },
        {
          id: 2,
          classification: ConversationClassification.ORDER,
          status: ConversationStatus.OPEN,
          assignee: null,
          thread_id: 1,
          author: { id: 2, name: 'Jane Doe', email: 'jane.doe@example.com' },
          subject: 'Hello, world!',
          comment: '@Jane Doe: Hello, world!',
          created_at: '2021-01-01T00:00:00Z',
          is_read: false,
        },
      ], object: undefined, message: 'Success', metadata: { page: 1, limit: 10, pages: 1, count: 0 } })));
    }

    return this.http.get<APIResponse<IMention>>(`${this.baseUrl}/comments/mentions`, { params: httpParams });
  }

  createComment(conversationId: number, message: string): Observable<APIResponse<any>> {
    if (this.useMockData) {
      return of(null).pipe(delay(1000), switchMap(() => of<APIResponse<any>>({ data: [], object: undefined, message: 'Success', metadata: { page: 1, limit: 10, pages: 1, count: 0 } })));
    }
    return this.http.post<APIResponse<any>>(`${this.baseUrl}/threads/${conversationId}/comments`, { body: message });
  }

  updateComment(conversationId: number, commentId: number, body: string): Observable<APIResponse<any>> {
    if (this.useMockData) {
      return of(null).pipe(delay(500), switchMap(() => of<APIResponse<any>>({ data: [], object: undefined, message: 'Success', metadata: { page: 1, limit: 10, pages: 1, count: 0 } })));
    }
    return this.http.put<APIResponse<any>>(`${this.baseUrl}/threads/${conversationId}/comments/${commentId}`, { body });
  }

  deleteComment(conversationId: number, commentId: number): Observable<APIResponse<any>> {
    if (this.useMockData) {
      return of(null).pipe(delay(500), switchMap(() => of<APIResponse<any>>({ data: [], object: undefined, message: 'Success', metadata: { page: 1, limit: 10, pages: 1, count: 0 } })));
    }
    return this.http.delete<APIResponse<any>>(`${this.baseUrl}/threads/${conversationId}/comments/${commentId}`);
  }
}
